import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { AuthContext } from '../App';
import { Calendar, ArrowLeft, MessageCircle, Facebook, Twitter, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SocialLoginButtons from '../components/SocialLoginButtons';
import ArticleSlideshow from '../components/ArticleSlideshow';
import ShareButtons from '../components/ShareButtons';
import AdSenseAd from '../components/AdSenseAd';

// Configure DOMPurify to allow iframes for embedded content
DOMPurify.addHook('uponSanitizeElement', (node, data) => {
  if (data.tagName === 'iframe') {
    const src = node.getAttribute('src') || '';
    // Only allow iframes from trusted sources
    const trustedDomains = [
      'youtube.com',
      'www.youtube.com',
      'youtu.be',
      'customer-assets-39nsmqrw.emergentagent.net',
      'customer-assets.emergentagent.com',
      'emergentagent.com'
    ];
    const isAllowed = trustedDomains.some(domain => src.includes(domain));
    if (!isAllowed) {
      node.parentNode?.removeChild(node);
    }
  }
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Use SITE_URL for production sharing (shelovesthee.com)
const SITE_URL = process.env.REACT_APP_SITE_URL || BACKEND_URL;

const ArticlePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, login, requiresNickname, promptNickname } = useContext(AuthContext);
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const loadedArticleId = article?.article_id;
  
  // Share URL that includes OG tags for social media previews
  // Uses SITE_URL (shelovesthee.com) for production
  const shareUrl = `${SITE_URL}/api/share/article/${id}`;

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const [articleRes, commentsRes] = await Promise.all([
          axios.get(`${API}/articles/${id}`),
          axios.get(`${API}/comments/${id}`)
        ]);
        
        // If article has a redirect_url, redirect to the custom component page
        if (articleRes.data.redirect_url) {
          navigate(articleRes.data.redirect_url, { replace: true });
          return;
        }
        
        setArticle(articleRes.data);
        setComments(commentsRes.data);
      } catch (error) {
        console.error('Error fetching article:', error);
        toast.error('Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id, navigate]);

  useEffect(() => {
    if (!loadedArticleId || window.location.hash) return undefined;
    const resetToTop = () => window.scrollTo(0, 0);
    resetToTop();
    const frame = window.requestAnimationFrame(resetToTop);
    const settleTimer = window.setTimeout(resetToTop, 500);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer);
    };
  }, [loadedArticleId]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to comment');
      return;
    }
    
    // Check if user needs to set nickname first
    if (requiresNickname()) {
      promptNickname();
      toast.info('Please set a nickname before commenting');
      return;
    }
    
    if (!newComment.trim()) return;

    try {
      const response = await axios.post(
        `${API}/comments`,
        { article_id: id, content: newComment },
        { withCredentials: true }
      );
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Comment posted!');
    } catch (error) {
      console.error('Error posting comment:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to post comment';
      if (errorMsg.includes('nickname')) {
        promptNickname();
      }
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="loading">Loading article...</div>;
  }

  if (!article) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="not-found">Article not found</div>;
  }

  const articleUrl = `${SITE_URL}/news/${article.article_id}`;
  const imageUrl = article.image_url || `${SITE_URL}/og-default.jpg`;
  const pageTitle = `${article.title} | SheLovesThee Podcast`;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="article-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={article.excerpt || ''} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:title" content={article.title || ''} />
        <meta property="og:description" content={article.excerpt || ''} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="SheLovesThee Podcast" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={shareUrl} />
        <meta name="twitter:title" content={article.title || ''} />
        <meta name="twitter:description" content={article.excerpt || ''} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="twitter:site" content="@SheLovesThee_" />
      </Helmet>
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/news" className="inline-flex items-center text-[#007AFF] hover:underline mb-6" data-testid="back-to-news">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to News
        </Link>

        <article className="bg-white shadow-lg overflow-hidden mb-8">
          {article.image_url && (
            <div className="flex justify-center bg-slate-100 p-4">
              <img src={article.image_url} alt={article.title} className="max-w-full max-h-[500px] object-contain" data-testid="article-image" />
            </div>
          )}

          <div className="p-8">
            <div className="flex items-center space-x-4 mb-4 text-sm text-slate-600">
              <span className="font-bold uppercase tracking-wider text-[#007AFF]" data-testid="article-category">{article.category}</span>
              <span className="flex items-center" data-testid="article-date">
                <Calendar className="h-4 w-4 mr-1" />
                {new Date(article.created_at).toLocaleDateString()}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-[#001f3f] mb-4 heading-font" data-testid="article-title">
              {article.title}
            </h1>

            {/* Share Buttons */}
            <div className="flex items-center flex-wrap gap-3 mb-6">
              <span className="text-sm font-semibold text-slate-600">Share:</span>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-[#1877F2] text-white rounded-full hover:opacity-80 transition-opacity"
                data-testid="share-facebook"
                title="Share on Facebook"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}&via=SheLovesThee_`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-black text-white rounded-full hover:opacity-80 transition-opacity"
                data-testid="share-twitter"
                title="Share on X/Twitter"
              >
                <Twitter className="h-4 w-4" />
              </a>
              <a
                href={`https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(article.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-[#FF4500] text-white rounded-full hover:opacity-80 transition-opacity"
                data-testid="share-reddit"
                title="Share on Reddit"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                </svg>
              </a>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl);
                  toast.success('Share link copied! Paste anywhere for preview with title & image.');
                }}
                className="p-2 bg-[#007AFF] text-white rounded-full hover:opacity-80 transition-opacity"
                data-testid="share-copy"
                title="Copy shareable link"
              >
                <Share2 className="h-4 w-4" />
              </button>
            </div>

            <p className="text-lg text-slate-600 mb-6 italic" data-testid="article-excerpt">{article.excerpt}</p>

            <div className="prose max-w-none text-slate-700 leading-relaxed" data-testid="article-content">
              {/* Check if content is HTML (contains HTML tags) */}
              {article.content.includes('<') && article.content.includes('>') ? (
                <>
                  {/* Check for Slideshow shortcode: [SLIDESHOW:url1,url2,url3] */}
                  {article.content.includes('[SLIDESHOW:') && (() => {
                    const slideshowMatch = article.content.match(/\[SLIDESHOW:([^\]]+)\]/);
                    if (slideshowMatch) {
                      const urls = slideshowMatch[1].split(',').map(url => url.trim());
                      const images = urls.map((url, i) => ({ src: url, alt: `Slide ${i + 1}` }));
                      return <ArticleSlideshow images={images} autoScroll={true} interval={4000} />;
                    }
                    return null;
                  })()}
                  
                  {/* Render HTML content with enhanced sanitization */}
                  <div 
                    className="article-html-content"
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(
                        // Remove script tags and shortcodes for safety
                        article.content
                          .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
                          .replace(/\[SLIDESHOW:[^\]]+\]/g, ''),
                        { 
                          ADD_TAGS: ['iframe', 'style'], 
                          ADD_ATTR: ['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'title', 'style', 'class', 'id', 'data-player', 'role', 'aria-label', 'aria-live'],
                          ALLOW_DATA_ATTR: true
                        }
                      ) 
                    }} 
                  />
                </>
              ) : (
                // Original markdown-style parsing for non-HTML content
                article.content.split('\n').map((para, idx) => {
                // Handle images: ![alt](url)
                const imageMatch = para.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
                if (imageMatch) {
                  const [, alt, src] = imageMatch;
                  // Check if this is the first image in the article (idx < 20 means early in content)
                  const isHeroImage = idx < 20;
                  return (
                    <div key={`img-${idx}`} className={`my-6 ${isHeroImage ? 'flex justify-center' : ''}`}>
                      <img 
                        src={src} 
                        alt={alt} 
                        className={`rounded-lg shadow-md ${isHeroImage ? 'w-auto max-w-full max-h-[600px] object-contain' : 'w-full max-w-2xl mx-auto'}`}
                      />
                    </div>
                  );
                }
                
                // Handle image captions (italicized text starting with *)
                if (para.startsWith('*') && para.endsWith('*') && !para.startsWith('**')) {
                  return (
                    <p key={`caption-${idx}`} className="text-sm text-slate-500 italic text-center -mt-4 mb-6">
                      {para.replace(/^\*|\*$/g, '')}
                    </p>
                  );
                }
                
                // Handle horizontal rules
                if (para.trim() === '---') {
                  return <hr key={`hr-${idx}`} className="my-6 border-slate-200" />;
                }
                
                // Handle tables
                if (para.startsWith('|') && para.endsWith('|')) {
                  const cells = para.split('|').filter(c => c.trim());
                  // Check if it's a separator row
                  if (cells.every(c => c.trim().match(/^-+$/))) {
                    return null;
                  }
                  const colCount = cells.length;
                  const gridCols = colCount <= 3 ? 'grid-cols-3' : colCount === 4 ? 'grid-cols-4' : colCount === 5 ? 'grid-cols-5' : 'grid-cols-6';
                  return (
                    <div key={`table-row-${idx}`} className={`grid ${gridCols} gap-2 border-b border-slate-200 py-2 text-sm`}>
                      {cells.map((cell, cellIdx) => (
                        <span key={`cell-${idx}-${cellIdx}`} className={cellIdx === 0 ? 'font-semibold' : ''} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cell.trim().replace(/\*\*/g, ''), { ADD_TAGS: ['iframe'], ADD_ATTR: ['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'title', 'style'] }) }} />
                      ))}
                    </div>
                  );
                }
                // Handle headers (bold markers)
                if (para.startsWith('**') && para.endsWith('**')) {
                  return <h3 key={`header-${idx}`} className="text-xl font-bold text-[#001f3f] mt-6 mb-3">{para.replace(/\*\*/g, '')}</h3>;
                }
                // Handle key details section
                if (para.startsWith('- **')) {
                  const match = para.match(/- \*\*([^*]+)\*\*(.+)/);
                  if (match) {
                    return (
                      <p key={`detail-${idx}`} className="mb-2 pl-4">
                        <strong>{match[1]}</strong>{match[2]}
                      </p>
                    );
                  }
                }
                // Regular bullet points with links
                if (para.startsWith('- ')) {
                  const bulletContent = para.substring(2);
                  // Check for markdown links [text](url)
                  const linkMatch = bulletContent.match(/\[([^\]]+)\]\(([^)]+)\)/);
                  if (linkMatch) {
                    const [, linkText, linkUrl] = linkMatch;
                    return (
                      <p key={`bullet-link-${idx}`} className="mb-2 pl-4">
                        • <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="text-[#007AFF] hover:underline">{linkText}</a>
                      </p>
                    );
                  }
                  // Process any HTML spans for red bold text
                  return <p key={`bullet-${idx}`} className="mb-2 pl-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize('• ' + bulletContent, { ADD_TAGS: ['iframe'], ADD_ATTR: ['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'title', 'style'] }) }} />;
                }
                // Handle iframes (dashboards, embeds)
                if (para.includes('<iframe')) {
                  const srcMatch = para.match(/src="([^"]+)"/);
                  const titleMatch = para.match(/title="([^"]+)"/);
                  const src = srcMatch ? srcMatch[1] : '';
                  const title = titleMatch ? titleMatch[1] : 'Embedded content';
                  // Only render iframes from trusted sources
                  const trustedDomains = ['customer-assets', 'emergentagent', 'youtube.com'];
                  const isTrusted = trustedDomains.some(domain => src.includes(domain));
                  if (isTrusted && src) {
                    return (
                      <div key={`iframe-${idx}`} className="my-6">
                        <iframe
                          src={src}
                          width="100%"
                          height="700"
                          style={{ border: 'none', borderRadius: '8px' }}
                          title={title}
                        />
                      </div>
                    );
                  }
                  return null;
                }
                // Regular paragraphs
                if (para.trim()) {
                  // Replace inline bold and links
                  let processed = para.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
                  // Replace markdown links with HTML links
                  processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-[#007AFF] hover:underline">$1</a>');
                  return <p key={`para-${idx}`} className="mb-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processed, { ADD_TAGS: ['iframe'], ADD_ATTR: ['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'title', 'style'] }) }} />;
                }
                return null;
              })
              )}
            </div>

            {article.youtube_url && (
              <div className="mt-8" data-testid="article-youtube">
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${extractYouTubeId(article.youtube_url)}`}
                    title="YouTube video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            )}

            {/* Share Buttons */}
            <div className="mt-8 pt-6 border-t border-slate-200">
              <h3 className="text-sm font-bold uppercase text-slate-500 mb-3">Share This Article</h3>
              <ShareButtons 
                title={article.title} 
                url={window.location.href}
                description={article.excerpt}
              />
            </div>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-sm text-slate-600">By {article.author}</p>
            </div>
          </div>
        </article>

        <div className="ad-unit mb-8" data-testid="article-ad">
          <AdSenseAd 
            adSlot="2800195719"
            adFormat="horizontal"
            style={{ minHeight: '90px' }}
            label="Advertisement"
          />
        </div>

        <div className="bg-white shadow-lg p-8" data-testid="comments-section">
          <h2 className="text-2xl font-bold uppercase mb-6 heading-font text-[#001f3f]" data-testid="comments-title">
            Comments ({comments.length})
          </h2>

          {user ? (
            <form onSubmit={handleCommentSubmit} className="mb-8" data-testid="comment-form">
              <div className="flex items-center space-x-2 mb-3">
                {user.picture && (
                  <img src={user.picture} alt={user.nickname || user.name} className="h-8 w-8 rounded-full" />
                )}
                <span className="font-semibold text-[#001f3f]">{user.nickname || user.name}</span>
                {!user.nickname && (
                  <button
                    type="button"
                    onClick={() => promptNickname()}
                    className="text-xs bg-[#DC2626] text-white px-2 py-1 rounded-full hover:bg-[#b91c1c] transition-colors"
                    data-testid="set-nickname-btn-article"
                  >
                    Set Nickname
                  </button>
                )}
              </div>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={user.nickname ? "Share your thoughts..." : "Set a nickname to comment..."}
                className="w-full p-4 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
                rows="4"
                data-testid="comment-textarea"
                disabled={!user.nickname}
              />
              <Button 
                type="submit"
                className="mt-3 bg-[#007AFF] text-white px-6 py-2 rounded-full font-bold uppercase hover:scale-105 transition-transform disabled:opacity-50"
                data-testid="comment-submit-button"
                disabled={!user.nickname || !newComment.trim()}
              >
                Post Comment
              </Button>
            </form>
          ) : (
            <div className="mb-8 p-8 bg-gradient-to-br from-slate-100 to-slate-50 rounded-xl text-center border border-slate-200" data-testid="login-prompt">
              <MessageCircle className="h-12 w-12 text-[#001f3f] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#001f3f] mb-2">Join the Conversation</h3>
              <p className="mb-6 text-slate-600">Sign in with your favorite platform to comment</p>
              <SocialLoginButtons />
            </div>
          )}

          <div className="space-y-4">
            {comments.length === 0 ? (
              <p className="text-center text-slate-600 py-8" data-testid="no-comments">No comments yet. Be the first to share your thoughts!</p>
            ) : (
              comments.map(comment => (
                <div key={comment.comment_id} className="border-b border-slate-200 pb-4" data-testid={`comment-${comment.comment_id}`}>
                  <div className="flex items-start space-x-3">
                    {comment.user_picture && (
                      <img src={comment.user_picture} alt={comment.user_name} className="h-10 w-10 rounded-full" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-bold text-sm">{comment.user_name}</span>
                        <span className="text-xs text-slate-500">
                          {new Date(comment.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-slate-700">{comment.content}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Back to News Link */}
          <div className="mt-12 pt-8 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-500 uppercase tracking-wider mb-4">Continue Reading</p>
            <Link 
              to="/news"
              className="inline-flex items-center gap-2 bg-[#007AFF] text-white px-6 py-3 rounded-full font-bold uppercase text-sm hover:scale-105 transition-transform"
              data-testid="back-to-news-link"
            >
              📰 Back to News
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const extractYouTubeId = (url) => {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|live\/))([\w-]+)/);
  return match ? match[1] : '';
};

export default ArticlePage;